import React, { useState } from 'react';
import { useDarkMode } from '../../components/darkmode/DarkModeContext';
import Navbar from '../../components/navbar/navbar';
import Footer from '../../components/footer/index';
import Section from '../../components/layanan/IP_ASN/section';
import { 
  Globe, 
  Server, 
  Shield, 
  Network, 
  Settings, 
  Users, 
  CheckCircle, 
  MapPin, 
  Clock, 
  ArrowRight, 
  MessageCircle,
  Database,
  Cpu,
  Lock,
  Zap,
  Eye,
  BarChart3
} from 'lucide-react';

const IPASNPage = () => {
  const { isDarkMode } = useDarkMode();
  const [activeTab, setActiveTab] = useState('ip-allocation');

  const ipAsnFeatures = [
    {
      icon: <Globe className="w-8 h-8" />,
      title: "Alokasi IP Global",
      description: "Dapatkan blok IP yang diakui secara global dan dapat digunakan di seluruh internet",
      color: "bg-blue-500"
    },
    {
      icon: <Network className="w-8 h-8" />,
      title: "ASN Registration",
      description: "Registrasi ASN untuk identifikasi unik jaringan Anda dalam sistem routing global",
      color: "bg-purple-500"
    },
    {
      icon: <Server className="w-8 h-8" />,
      title: "BGP Management",
      description: "Manajemen Border Gateway Protocol untuk routing optimal dan redundansi jaringan",
      color: "bg-green-500"
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "IP Whois Management",
      description: "Kelola informasi whois untuk transparansi dan kepatuhan regulasi internet",
      color: "bg-red-500"
    },
    {
      icon: <Settings className="w-8 h-8" />,
      title: "Route Filtering",
      description: "Implementasi filter routing untuk keamanan dan optimisasi traffic",
      color: "bg-yellow-500"
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "Network Monitoring",
      description: "Monitoring real-time untuk status BGP announcement dan reachability",
      color: "bg-indigo-500"
    }
  ];

  const useCases = {
    'ip-allocation': [
      {
        title: "Public IP Block",
        description: "Alokasi blok IP publik untuk hosting, data center, dan infrastruktur internet",
        image: "/image/ipblock.png",
        benefits: ["IPv4 dan IPv6 tersedia", "Fully routable globally", "RIPE/APNIC registered"]
      },
      {
        title: "Provider Independent (PI) Space",
        description: "IP space yang tidak terikat pada ISP tertentu untuk fleksibilitas routing",
        image: "/image/provider.png",
        benefits: ["Multi-homing capability", "ISP independence", "Route control"]
      }
    ],
    'asn-services': [
      {
        title: "ASN Registration",
        description: "Registrasi Autonomous System Number untuk identifikasi jaringan yang unik",
        image: "/image/asn.png",
        benefits: ["16-bit dan 32-bit ASN", "Global recognition", "BGP peering ready"]
      },
      {
        title: "BGP Peering Setup",
        description: "Setup dan konfigurasi BGP peering dengan major internet exchanges",
        image: "/image/BGP.png",
        benefits: ["Multiple peering points", "Route optimization", "Redundancy setup"]
      }
    ],
    'enterprise': [
      {
        title: "Enterprise Network Infrastructure",
        description: "Solusi lengkap untuk infrastruktur jaringan enterprise dengan IP dan ASN",
        image: "/image/enterprise.png",
        benefits: ["Custom IP allocation", "Dedicated ASN", "24/7 NOC support"]
      },
      {
        title: "Data Center Solutions",
        description: "Layanan IP dan ASN khusus untuk operator data center dan cloud provider",
        image: "/image/datacenter1.png",
        benefits: ["Large IP blocks", "Multi-site BGP", "Anycast capability"]
      }
    ]
  };

  const ipAsnPackages = [
    {
      id: 1,
      name: "IP Allocation Starter",
      popular: false,
      color: "from-blue-500 to-blue-700",
      ipRange: "/24 IPv4 Block",
      asn: "Optional",
      support: "Business Hours",
      features: [
        "256 IPv4 Addresses",
        "RIPE/APNIC Registration",
        "Basic Whois Management",
        "Email Support",
        "Setup Documentation",
        "30-Day Migration Support"
      ]
    },
    {
      id: 2,
      name: "ASN + IP Professional",
      popular: true,
      color: "from-green-500 to-green-700",
      ipRange: "/22 IPv4 Block",
      asn: "Dedicated ASN",
      support: "24/7 Support",
      features: [
        "1024 IPv4 Addresses",
        "Dedicated 32-bit ASN",
        "BGP Setup & Configuration",
        "Route Object Management",
        "NOC Monitoring",
        "IPv6 /48 Allocation",
        "Multiple Peering Points"
      ]
    },
    {
      id: 3,
      name: "Enterprise Network",
      popular: false,
      color: "from-purple-500 to-purple-700",
      ipRange: "Custom Allocation",
      asn: "Multiple ASN",
      support: "Dedicated NOC",
      features: [
        "Custom IP Block Size",
        "Multiple ASN Options",
        "Anycast Implementation",
        "Advanced BGP Policies",
        "DDoS Mitigation",
        "Global Route Optimization",
        "Dedicated Account Manager",
        "SLA 99.9% Uptime"
      ]
    }
  ];

  const technicalSpecs = [
    {
      category: "IPv4 Allocation",
      specs: [
        { name: "Minimum Block Size", value: "/24 (256 IPs)" },
        { name: "Registry", value: "APNIC/RIPE" },
        { name: "Assignment Type", value: "PA/PI Available" },
        { name: "Global Routing", value: "Guaranteed" }
      ]
    },
    {
      category: "IPv6 Support",
      specs: [
        { name: "Minimum Block Size", value: "/48 (281T IPs)" },
        { name: "Delegation", value: "Direct from RIR" },
        { name: "Reverse DNS", value: "Included" },
        { name: "Auto-configuration", value: "SLAAC Support" }
      ]
    },
    {
      category: "ASN Services",
      specs: [
        { name: "ASN Type", value: "16-bit & 32-bit" },
        { name: "Registration Time", value: "2-4 weeks" },
        { name: "BGP Communities", value: "Custom Support" },
        { name: "Route Objects", value: "RPSL Maintained" }
      ]
    },
    {
      category: "Network Performance",
      specs: [
        { name: "Global Peering", value: "500+ Networks" },
        { name: "Latency (Regional)", value: "< 5ms" },
        { name: "Uptime SLA", value: "99.9%" },
        { name: "DDoS Protection", value: "Up to 100Gbps" }
      ]
    }
  ];

  const peeringPartners = [
    { name: "Cloudflare", asn: "AS13335", location: "Global", type: "Content" },
    { name: "Google", asn: "AS15169", location: "Global", type: "Content" },
    { name: "Akamai", asn: "AS20940", location: "Global", type: "CDN" },
    { name: "Amazon", asn: "AS16509", location: "Global", type: "Cloud" },
    { name: "Microsoft", asn: "AS8075", location: "Global", type: "Cloud" },
    { name: "Facebook", asn: "AS32934", location: "Global", type: "Social" }
  ];

  const testimonials = [
    {
      name: "PT. Hosting Indonesia",
      role: "CTO",
      company: "Web Hosting Provider",
      rating: 5,
      comment: "Layanan IP dan ASN sangat membantu ekspansi bisnis hosting kami. Setup BGP berjalan lancar dan support teknis sangat responsif.",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=60&h=60&fit=crop&crop=face"
    },
    {
      name: "DataCenter Jakarta",
      role: "Network Manager", 
      company: "Data Center Operator",
      rating: 5,
      comment: "Alokasi IP block dan ASN memungkinkan kami untuk memberikan layanan yang lebih baik kepada klien dengan routing yang optimal.",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=60&h=60&fit=crop&crop=face"
    },
    {
      name: "Tech Startup Solutions",
      role: "Infrastructure Lead",
      company: "Cloud Provider",
      rating: 5,
      comment: "Dengan ASN sendiri, kami bisa melakukan multi-homing dan mendapatkan redundansi yang diperlukan untuk layanan cloud kami.",
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=60&h=60&fit=crop&crop=face"
    }
  ];

  // Fungsi untuk mengarahkan ke WhatsApp dengan pesan otomatis
  const contactViaWhatsApp = (packageName = "IP dan ASN Service") => {
    const phoneNumber = "6287778882022";
    const message = `Halo, saya tertarik dengan layanan ${packageName}. Bisakah saya mendapatkan informasi lebih lanjut mengenai alokasi IP dan registrasi ASN?`;
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/${phoneNumber}?text=${encodedMessage}`, '_blank');
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDarkMode ? 'bg-gray-900 text-gray-100' : 'bg-gray-50 text-gray-900'
    }`}>
      
      <Navbar />
      
      {/* Hero Section */}
      <Section />

      {/* Features Section */}
      <section className={`py-16 px-4 transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-800' : 'bg-white'
      }`}>
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className={`text-3xl font-bold mb-4 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Mengapa Memilih Layanan IP dan ASN Kami?
            </h2>
            <p className={`text-lg ${
              isDarkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Solusi lengkap untuk kebutuhan IP allocation dan ASN management enterprise
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-[#1E9B4C] to-green-600 mx-auto rounded-full mt-4"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {ipAsnFeatures.map((feature, index) => (
              <div
                key={index}
                className={`group p-6 rounded-xl shadow-lg overflow-hidden transform hover:scale-105 hover:shadow-2xl transition-all duration-300 ${
                  isDarkMode 
                    ? 'bg-gray-800 border border-gray-700 hover:border-[#1E9B4C] shadow-gray-900/50' 
                    : 'bg-white border border-gray-100 hover:border-[#1E9B4C] shadow-gray-300/20'
                }`}
              >
                <div className={`w-16 h-16 rounded-lg flex items-center justify-center text-white mb-4 ${
                  feature.color || 'bg-[#1E9B4C]'
                }`}>
                  {feature.icon}
                </div>

                <h3 className={`text-xl font-bold mb-3 transition-colors ${
                  isDarkMode
                    ? 'text-white group-hover:text-[#1E9B4C]'
                    : 'text-gray-900 group-hover:text-[#1E9B4C]'
                }`}>
                  {feature.title}
                </h3>

                <p className={`transition-colors ${
                  isDarkMode 
                    ? 'text-gray-300 group-hover:text-gray-200' 
                    : 'text-gray-600 group-hover:text-gray-700'
                }`}>
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className={`py-16 px-4 transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-900' : 'bg-gray-50'
      }`}>
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className={`text-3xl font-bold mb-4 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Solusi untuk Berbagai Kebutuhan Jaringan
            </h2>
            <p className={`text-lg ${
              isDarkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Temukan solusi yang tepat untuk kebutuhan spesifik Anda
            </p>
          </div>

          <div className="flex justify-center mb-8">
            <div className={`inline-flex rounded-lg p-1 shadow-lg ${
              isDarkMode 
                ? 'bg-gray-800 border border-gray-700' 
                : 'bg-white border border-gray-200'
            }`}>
              {Object.keys(useCases).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                    activeTab === tab
                      ? 'bg-[#1E9B4C] text-white shadow-lg'
                      : isDarkMode
                      ? 'text-gray-300 hover:text-white hover:bg-gray-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  {tab === 'ip-allocation'
                    ? 'IP Allocation'
                    : tab === 'asn-services'
                    ? 'ASN Services'
                    : 'Enterprise'}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {useCases[activeTab].map((useCase, index) => (
              <div
                key={index}
                className={`rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 ${
                  isDarkMode 
                    ? 'bg-gray-800 border border-gray-700 shadow-gray-900/50' 
                    : 'bg-white border border-gray-100 shadow-gray-300/20'
                }`}
              >
                <div className="relative">
                  <img
                    src={useCase.image}
                    alt={useCase.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className={`absolute inset-0 ${
                    isDarkMode 
                      ? 'bg-gradient-to-t from-gray-800/20 to-transparent' 
                      : 'bg-gradient-to-t from-white/20 to-transparent'
                  }`}></div>
                </div>
                <div className="p-6">
                  <h3 className={`text-xl font-bold mb-3 ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    {useCase.title}
                  </h3>
                  <p className={`mb-4 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    {useCase.description}
                  </p>
                  <div className="space-y-2">
                    {useCase.benefits.map((benefit, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-[#1E9B4C] flex-shrink-0" />
                        <span className={`text-sm ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                          {benefit}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technical Specifications */}
      <section className={`py-16 px-4 transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-800' : 'bg-white'
      }`}>
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className={`text-3xl font-bold mb-4 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Spesifikasi Teknis
            </h2>
            <p className={`text-lg ${
              isDarkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Detail teknis layanan IP allocation dan ASN management
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {technicalSpecs.map((spec, index) => (
              <div
                key={index}
                className={`p-6 rounded-xl border-2 transition-all duration-300 hover:border-[#1E9B4C] hover:shadow-lg transform hover:scale-105 ${
                  isDarkMode 
                    ? 'bg-gray-800 border-gray-700 shadow-gray-900/50' 
                    : 'bg-white border-gray-200 shadow-gray-300/20'
                }`}
              >
                <h3 className={`text-lg font-bold mb-4 text-[#1E9B4C]`}>
                  {spec.category}
                </h3>
                <div className="space-y-3">
                  {spec.specs.map((item, idx) => (
                    <div key={idx} className="flex flex-col">
                      <span className={`text-sm ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        {item.name}
                      </span>
                      <span className={`font-semibold ${
                        isDarkMode ? 'text-white' : 'text-gray-900'
                      }`}>
                        {item.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Peering Partners */}
      <section className={`py-16 px-4 transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-900' : 'bg-gray-50'
      }`}>
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className={`text-3xl font-bold mb-4 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Global Peering Partners
            </h2>
            <p className={`text-lg ${
              isDarkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Terhubung dengan provider terkemuka untuk routing optimal
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {peeringPartners.map((partner, index) => (
              <div
                key={index}
                className={`p-6 rounded-xl border-2 transition-all duration-300 hover:border-[#1E9B4C] hover:shadow-lg transform hover:scale-105 ${
                  isDarkMode 
                    ? 'bg-gray-800 border-gray-700 shadow-gray-900/50' 
                    : 'bg-white border-gray-200 shadow-gray-300/20'
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className={`font-bold text-lg ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                      {partner.name}
                    </h3>
                    <p className={`text-sm ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {partner.asn}
                    </p>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                    partner.type === 'Content' ? 'bg-blue-100 text-blue-800' :
                    partner.type === 'CDN' ? 'bg-purple-100 text-purple-800' :
                    partner.type === 'Cloud' ? 'bg-green-100 text-green-800' :
                    'bg-orange-100 text-orange-800'
                  }`}>
                    {partner.type}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-[#1E9B4C]" />
                  <span className={`text-sm ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    {partner.location}
                  </span>
                  <div className="ml-auto flex items-center gap-1">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-xs text-green-500 font-medium">Active</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className={`py-16 px-4 transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-900' : 'bg-gray-50'
      }`}>
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className={`text-3xl font-bold mb-4 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Paket Layanan IP dan ASN
            </h2>
            <p className={`text-lg ${
              isDarkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Pilih paket yang sesuai dengan kebutuhan infrastruktur jaringan Anda
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {ipAsnPackages.map((pkg) => (
              <div
                key={pkg.id}
                className={`relative rounded-2xl p-8 shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-2xl ${
                  pkg.popular
                    ? `bg-gradient-to-br ${pkg.color} text-white shadow-2xl transform scale-105`
                    : isDarkMode
                    ? 'bg-gray-800 text-white border border-gray-700 shadow-gray-900/50'
                    : 'bg-white text-gray-900 border border-gray-200 shadow-gray-300/20'
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-yellow-400 text-gray-900 px-4 py-1 rounded-full text-sm font-bold shadow-lg">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold mb-2">{pkg.name}</h3>
                  <div className="mb-4">
                    <span className="text-lg font-medium">Konsultasi untuk harga khusus</span>
                  </div>
                </div>

                <div className="mb-8">
                  <div className={`flex justify-between mb-2 pb-2 border-b ${
                    pkg.popular 
                      ? 'border-white/20' 
                      : isDarkMode 
                      ? 'border-gray-700' 
                      : 'border-gray-200'
                  }`}>
                    <span>IP Range</span>
                    <span className="font-bold">{pkg.ipRange}</span>
                  </div>
                  <div className={`flex justify-between mb-2 pb-2 border-b ${
                    pkg.popular 
                      ? 'border-white/20' 
                      : isDarkMode 
                      ? 'border-gray-700' 
                      : 'border-gray-200'
                  }`}>
                    <span>ASN</span>
                    <span className="font-bold">{pkg.asn}</span>
                  </div>
                  <div className={`flex justify-between mb-4 pb-2 border-b ${
                    pkg.popular 
                      ? 'border-white/20' 
                      : isDarkMode 
                      ? 'border-gray-700' 
                      : 'border-gray-200'
                  }`}>
                    <span>Support</span>
                    <span className="font-bold">{pkg.support}</span>
                  </div>

                  <ul className="space-y-3">
                    {pkg.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <CheckCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                          pkg.popular ? 'text-white' : 'text-[#1E9B4C]'
                        }`} />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <button
                  onClick={() => contactViaWhatsApp(pkg.name)}
                  className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center gap-2 transform hover:scale-105 ${
                    pkg.popular 
                      ? 'bg-white text-green-700 hover:bg-gray-100 shadow-lg' 
                      : 'bg-[#1E9B4C] text-white hover:bg-green-700 shadow-lg hover:shadow-xl'
                  }`}
                >
                  <MessageCircle className="w-5 h-5" />
                  Hubungi Kami
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={`py-16 px-4 transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-800' : 'bg-[#1E9B4C]'
      }`}>
        <div className="container mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Siap Mengoptimalkan Infrastruktur Jaringan Anda?
          </h2>
          <p className="text-xl text-green-100 mb-8 max-w-3xl mx-auto">
            Dapatkan konsultasi gratis dari tim ahli kami untuk solusi IP allocation dan ASN management yang tepat untuk bisnis Anda.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => contactViaWhatsApp("IP ASN Consultation")}
              className="bg-white text-[#1E9B4C] hover:bg-gray-100 font-semibold px-8 py-4 rounded-lg transition-colors duration-200 flex items-center gap-2 justify-center"
            >
              <MessageCircle className="w-5 h-5" />
              Konsultasi Gratis
            </button>
            <button 
              onClick={() => contactViaWhatsApp("IP ASN Enterprise Quote")}
              className="border-2 border-white text-white hover:bg-white hover:text-[#1E9B4C] font-semibold px-8 py-4 rounded-lg transition-colors duration-200 flex items-center gap-2 justify-center"
            >
              <ArrowRight className="w-5 h-5" />
              Request Quote
            </button>
          </div>
        </div>
      </section>
        
      <Footer />
    </div>
  );
};

export default IPASNPage;